import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { SimWorld } from '../../simulation/types/SimWorld';

interface EventLogPanelProps {
  world: SimWorld;
  isMobile?: boolean;
}

type LogFilter = 'all' | 'birth' | 'death' | 'duel' | 'partnership' | 'other';

// 仮想スクロール設定
const LOG_ITEM_HEIGHT = 37; // 各ログアイテムの高さ（px）
const BUFFER_COUNT = 10; // 上下に余分に描画するアイテム数

/**
 * イベントログビューア
 * シミュレーション中の全イベントを表示（仮想スクロール対応）
 */
export const EventLogPanel: React.FC<EventLogPanelProps> = ({ world, isMobile = false }) => {
  const [filter, setFilter] = useState<LogFilter>('all');
  const [autoScroll, setAutoScroll] = useState(true);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(400);
  const containerRef = useRef<HTMLDivElement>(null);
  const logEndRef = useRef<HTMLDivElement>(null);

  // コンテナサイズの監視
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateHeight = () => {
      setContainerHeight(container.clientHeight);
    };
    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);

  // スクロールハンドラ
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    setScrollTop(target.scrollTop);

    // 手動スクロールで自動スクロールを解除
    const isAtBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 50;
    if (!isAtBottom && autoScroll) {
      setAutoScroll(false);
    }
  }, [autoScroll]);

  // 自動スクロール
  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [world.logs, autoScroll]);

  // ログのフィルタリング
  const filteredLogs = world.logs.filter((log) => {
    if (filter === 'all') return true;
    const message = log.toLowerCase();

    switch (filter) {
      case 'birth':
        return message.includes('👶') || message.includes('生まれた');
      case 'death':
        return (
          message.includes('💀') ||
          message.includes('⚰️') ||
          message.includes('死亡') ||
          message.includes('衰弱死')
        );
      case 'duel':
        return message.includes('⚔️') || message.includes('勝利') || message.includes('デュエル');
      case 'partnership':
        return message.includes('💕') || message.includes('パートナー');
      case 'other':
        return (
          !message.includes('👶') &&
          !message.includes('💀') &&
          !message.includes('⚰️') &&
          !message.includes('⚔️') &&
          !message.includes('💕')
        );
      default:
        return true;
    }
  });

  // Dayを抽出する正規表現
  const extractDay = (log: string): number => {
    const match = log.match(/\[Day (\d+)\]/);
    return match ? parseInt(match[1], 10) : 0;
  };

  // メッセージ部分を抽出
  const extractMessage = (log: string): string => {
    return log.replace(/^\[Day \d+\] /, '');
  };

  // 表示用ログデータ
  const displayLogs = filteredLogs.map((log) => ({
    day: extractDay(log),
    message: extractMessage(log),
  }));

  // 仮想スクロール: 表示範囲の計算
  const totalHeight = displayLogs.length * LOG_ITEM_HEIGHT;
  const startIndex = Math.max(0, Math.floor(scrollTop / LOG_ITEM_HEIGHT) - BUFFER_COUNT);
  const visibleCount = Math.ceil(containerHeight / LOG_ITEM_HEIGHT) + BUFFER_COUNT * 2;
  const endIndex = Math.min(displayLogs.length, startIndex + visibleCount);
  const visibleLogs = displayLogs.slice(startIndex, endIndex);
  const offsetY = startIndex * LOG_ITEM_HEIGHT;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>📜 イベントログ</h3>
        <div style={styles.controls}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={autoScroll}
              onChange={(e) => setAutoScroll(e.target.checked)}
            />
            自動スクロール
          </label>
        </div>
      </div>

      <div style={styles.filterBar}>
        {(['all', 'birth', 'death', 'duel', 'partnership', 'other'] as LogFilter[]).map(
          (filterType) => (
            <button
              key={filterType}
              style={{
                ...styles.filterButton,
                ...(filter === filterType ? styles.filterButtonActive : {}),
              }}
              onClick={() => setFilter(filterType)}
            >
              {getFilterLabel(filterType)}
            </button>
          )
        )}
      </div>

      <div
        ref={containerRef}
        style={styles.logContainer}
        onScroll={handleScroll}
      >
        {displayLogs.length === 0 ? (
          <div style={styles.emptyMessage}>ログがありません</div>
        ) : (
          <div style={{ height: totalHeight, position: 'relative' }}>
            <div style={{ transform: `translateY(${offsetY}px)` }}>
              {visibleLogs.map((log, index) => (
                <div key={startIndex + index} style={styles.logEntry}>
                  <span style={styles.logDay}>Day {log.day}</span>
                  <span style={styles.logMessage}>{log.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div ref={logEndRef} />
      </div>

      <div style={styles.footer}>
        <span style={styles.logCount}>
          {filteredLogs.length} / {world.logs.length} イベント
        </span>
      </div>
    </div>
  );
};

function getFilterLabel(filter: LogFilter): string {
  switch (filter) {
    case 'all':
      return '🌐 すべて';
    case 'birth':
      return '👶 誕生';
    case 'death':
      return '💀 死亡';
    case 'duel':
      return '⚔️ デュエル';
    case 'partnership':
      return '💕 パートナー';
    case 'other':
      return '📌 その他';
    default:
      return filter;
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#1a1a2e',
    border: '2px solid #444',
    borderRadius: '8px',
    height: '500px',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#0f0f1e',
    borderBottom: '1px solid #444',
  },
  title: {
    margin: 0,
    color: '#fff',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  controls: {
    display: 'flex',
    gap: '12px',
  },
  checkboxLabel: {
    color: '#aaa',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
  },
  filterBar: {
    display: 'flex',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: '#16162a',
    borderBottom: '1px solid #444',
    flexWrap: 'wrap',
  },
  filterButton: {
    padding: '6px 12px',
    backgroundColor: '#2a2a3e',
    border: '1px solid #444',
    borderRadius: '4px',
    color: '#aaa',
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  filterButtonActive: {
    backgroundColor: '#3a3a5e',
    color: '#fff',
    borderColor: '#6a6a8e',
  },
  logContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 16px',
  },
  logEntry: {
    display: 'flex',
    gap: '12px',
    padding: '8px 0',
    borderBottom: '1px solid #2a2a3e',
  },
  logDay: {
    color: '#888',
    fontSize: '13px',
    minWidth: '70px',
    flexShrink: 0,
  },
  logMessage: {
    color: '#ddd',
    fontSize: '14px',
    flex: 1,
  },
  emptyMessage: {
    color: '#666',
    fontSize: '14px',
    textAlign: 'center',
    padding: '40px 20px',
  },
  footer: {
    padding: '8px 16px',
    backgroundColor: '#0f0f1e',
    borderTop: '1px solid #444',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  logCount: {
    color: '#888',
    fontSize: '12px',
  },
};
